import { createContext, useEffect, useState, useCallback } from "react";
import { useSnackbar } from 'notistack';
import { useNavigate } from "react-router-dom";
import api from '../api/api';

export const ShopContext = createContext();

const ShopContextProvider = (props) => {

    const currency = '$';
    const delivery_fee = 10;

    const [search, setSearch] = useState('');
    const [showSearch, setShowSearch] = useState(false);
    const [cartItems, setCartItems] = useState({});
    const [cartDetails, setCartDetails] = useState([]);
    const [token, setToken] = useState(localStorage.getItem('token'));
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();
    const [products, setProducts] = useState([]);
    const [productData, setProductData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchCart = useCallback(async () => {
        if (!token) return;
        try {
            const response = await api.get('/api/cart');
            const cartData = response.data;
            setCartDetails(cartData);

            const fetchedCart = cartData.reduce((acc, item) => {
                if (!acc[item.product_id]) {
                    acc[item.product_id] = {};
                }
                acc[item.product_id][item.size] = item.quantity;
                return acc;
            }, {});
            setCartItems(fetchedCart);
        } catch (error) {
            console.error('Failed to fetch cart:', error);
            setCartDetails([]);
            if (error.response && error.response.status === 401) {
                enqueueSnackbar('Session expired. Please log in again.', { variant: 'error' });
                localStorage.removeItem('token');
                setToken(null);
                navigate('/login');
            }
        }
    }, [token, navigate, enqueueSnackbar]);

    const addToCart = async (itemId, size) => {
        if (!size) {
            enqueueSnackbar('Select Product Size', { variant: 'error' });
            return;
        }

        if (token) {
            try {
                await api.post('/api/cart/add', { 
                    productId: itemId, 
                    size, 
                    quantity: 1 // Explicitly set quantity to 1
                });
                await fetchCart();
                // Success handled silently - no snackbar
            } catch (error) {
                console.error('Failed to add to cart:', error);
                const errorMessage = error.response?.data?.message || 'Failed to add item to cart.';
                enqueueSnackbar(errorMessage, { variant: 'error' });
            }
        } else {
            enqueueSnackbar('Please log in to add items to your cart.', { variant: 'info' });
            navigate('/login');
        }
    };

    const updateQuantity = async (itemId, size, quantity) => {
        if (token) {
            try {
                await api.put('/api/cart/update', { productId: itemId, size, quantity });
                await fetchCart();
            } catch (error) {
                console.error('Failed to update cart quantity:', error);
                enqueueSnackbar('Failed to update item quantity.', { variant: 'error' });
            }
        }
    };

    const removeFromCart = async (itemId, size) => {
        if (token) {
            try {
                await api.delete('/api/cart/remove', { data: { productId: itemId, size } });
                await fetchCart();
            } catch (error) {
                console.error('Failed to remove from cart:', error);
                enqueueSnackbar('Failed to remove item from cart.', { variant: 'error' });
            }
        }
    };

    const placeOrder = async () => {
        if (getCartCount() === 0) {
            enqueueSnackbar('Your cart is empty.', { variant: 'error' });
            return;
        }

        if (!token) {
            enqueueSnackbar('You must be logged in to place an order.', { variant: 'error' });
            navigate('/login');
            return;
        }

        try {
            const response = await api.post('/api/orders', {});

            if (response.status === 201) {
                enqueueSnackbar('Order placed successfully!', { variant: 'success' });
                setCartItems({});
                setCartDetails([]);
                navigate('/orders');
            }
        } catch (error) {
            enqueueSnackbar('Failed to place order. Please try again.', { variant: 'error' });
            console.error('Failed to place order:', error);
        }
    };

    const getCartCount = () => {
        return cartDetails.reduce((total, item) => total + item.quantity, 0);
    };

    const getCartAmount = () => {
        return cartDetails.reduce((total, item) => {
            return total + (item.product_price * item.quantity);
        }, 0);
    };

    const getProductsData = useCallback(async () => {
        try {
            const response = await api.get('/api/products');
            setProducts(response.data.products);
        } catch (error) {
            console.error('Error fetching products:', error);
        }
    }, []);

    useEffect(() => {
        const handleAuthChange = () => {
            const currentToken = localStorage.getItem('token');
            setToken(currentToken);
        };
        window.addEventListener('authChange', handleAuthChange);
        getProductsData();
        return () => window.removeEventListener('authChange', handleAuthChange);
    }, [getProductsData]);

    useEffect(() => {
        if (token) {
            fetchCart();
        } else {
            setCartItems({});
            setCartDetails([]);
        }
    }, [token, fetchCart]);

    const getProductDataById = useCallback(async (productId) => {
        setLoading(true);
        setError(null);
        try {
            const response = await api.get(`/api/products/${productId}`);
            setProductData(response.data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    const contextValue = {
        token,
        setToken,
        products,
        cartItems,
        cartDetails,
        addToCart,
        removeFromCart,
        updateQuantity,
        getCartCount,
        getCartAmount,
        currency,
        delivery_fee,
        search,
        setSearch,
        showSearch,
        setShowSearch,
        productData,
        loading,
        error,
        getProductDataById,
        enqueueSnackbar,
        placeOrder,
    };

    return (
        <ShopContext.Provider value={contextValue}>
            {props.children}
        </ShopContext.Provider>
    );
};

export default ShopContextProvider;